// src/component/HomeProductShelf.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ProductCard } from "./HomeProductShelfCard";

/**
 * Полка товаров:
 * - показывает только «в наличии»
 * - бесконечная карусель без “отмотки”
 * - свайп/стрелки по 1 карточке
 * - клик по карточке → /price?q=<название>&openBy=search (модалку откроет прайс-страница)
 */
export default function HomeProductShelf({ title, items = [], loading, error }) {
  const contRef = useRef(null);
  const trackRef = useRef(null);
  const navigate = useNavigate();

  const itemsSafe = Array.isArray(items) ? items : [];
  const baseReady = !loading && !error && itemsSafe.length > 0;

  // фильтр «в наличии»
  const isNo = (s) => ["false", "0", "no", "нет", "нет в наличии", "out of stock", "sold out"].includes(s);
  const isYes = (s) => ["true", "1", "yes", "да", "есть", "в наличии", "available", "in stock"].includes(s);
  const isInStock = (p) => {
    if (!p) return false;
    const flags = [p.in_stock, p.available, p.is_available, p.is_in_stock];
    if (flags.some((v) => v === true || v === 1)) return true;
    const qtys = [p.stock, p.quantity, p.qty, p.inventory, p.stock_total];
    if (qtys.some((n) => typeof n === "number" && n > 0)) return true;
    const str = [p.availability, p.stock_status, p.status, p.state]
      .filter((v) => typeof v === "string")
      .map((v) => v.trim().toLowerCase());
    if (str.some(isNo)) return false;
    if (str.some(isYes)) return true;
    return false;
  };

  const inStock = useMemo(() => itemsSafe.filter(isInStock), [itemsSafe]);
  const data = useMemo(() => (baseReady ? inStock : []), [baseReady, inStock]);

  // размеры ленты
  const [gap, setGap] = useState(12);
  const [perView, setPerView] = useState(2);
  const [cardW, setCardW] = useState(240);

  useEffect(() => {
    const measure = () => {
      const cont = contRef.current;
      const tr = trackRef.current;
      if (!cont) return;
      const w = cont.clientWidth || 0;
      const pv = w < 640 ? 2 : w < 1024 ? 3 : w < 1280 ? 4 : 5;
      setPerView(pv);
      const g = tr ? parseFloat(getComputedStyle(tr).gap || "12") : 12;
      setGap(Number.isFinite(g) ? g : 12);
      const cw = Math.floor((w - (pv - 1) * (Number.isFinite(g) ? g : 12)) / pv);
      setCardW(Math.max(220, cw));
    };
    measure();
    const onR = () => measure();
    window.addEventListener("resize", onR);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && contRef.current) ro.observe(contRef.current);
    return () => {
      window.removeEventListener("resize", onR);
      ro?.disconnect();
    };
  }, []);

  // перетасовка (детерминированная)
  const shuffled = useMemo(() => {
    if (!data.length) return [];
    const arr = [...data];
    let s = (arr.length * 9301 + 49297) >>> 0;
    const rnd = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 2 ** 32);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [data]);

  // клоны для бесконечности
  const clones = Math.max(2, perView + 1);
  const extended = useMemo(() => {
    if (!shuffled.length) return [];
    const head = shuffled.slice(-clones);
    const tail = shuffled.slice(0, clones);
    return [...head, ...shuffled, ...tail];
  }, [shuffled, clones]);

  // индекс/анимация/drag
  const [idx, setIdx] = useState(clones);
  const [withAnim, setWithAnim] = useState(true);
  const [dragX, setDragX] = useState(0);
  const step = cardW + gap;

  useEffect(() => {
    setWithAnim(false);
    setIdx(clones);
    requestAnimationFrame(() => setWithAnim(true));
  }, [clones]);

  const teleport = (nextIdx) => {
    if (!trackRef.current) return;
    setWithAnim(false);
    flushSync(() => setIdx(nextIdx));
    // eslint-disable-next-line no-unused-expressions
    trackRef.current.offsetHeight; // reflow
    setWithAnim(true);
  };

  const onTransitionEnd = useCallback(() => {
    const realLen = shuffled.length;
    if (!realLen) return;
    if (idx >= clones + realLen) teleport(idx - realLen);
    else if (idx < clones) teleport(idx + realLen);
  }, [idx, clones, shuffled.length]);

  const go = (d) => {
    if (!extended.length || !step) return;
    setWithAnim(true);
    setDragX(0);
    setIdx((v) => v + (d > 0 ? 1 : -1));
  };

  // drag/свайп
  const startX = useRef(0);
  const dragging = useRef(false);
  const onStart = (x) => {
    dragging.current = true;
    setWithAnim(false);
    startX.current = x;
  };
  const onMove = (x) => {
    if (!dragging.current) return;
    setDragX(x - startX.current);
  };
  const onEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    const threshold = Math.max(30, step * 0.25);
    const d = dragX <= -threshold ? 1 : dragX >= threshold ? -1 : 0;
    setWithAnim(true);
    setDragX(0);
    if (d !== 0) go(d);
  };

  // блокируем переход по <a> внутри карточки
  const preventLinkNav = (e) => {
    let el = e.target;
    if (el && el.nodeType !== 1) el = el.parentElement;
    if (el && typeof el.closest === "function" && el.closest("a")) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // из товара собираем поисковую строку
  const queryFromProduct = (p) => {
    const raw = p?.name || p?.title || p?.product_name || p?.model || p?.slug || "";
    return String(raw).replace(/\s+/g, " ").replace(/[(){}\[\]]/g, "").trim();
  };

  // клик → уходим на прайс с q и маркером openBy=search
  const onCardClick = (p) => {
    if (Math.abs(dragX) > 5 || dragging.current) return; // не открывать после свайпа
    const q = queryFromProduct(p);
    if (!q) return;
    navigate(`/price?q=${encodeURIComponent(q)}&openBy=search`);
  };

  // скелетоны
  const Skeleton = () => (
    <div className="animate-pulse rounded-xl bg-white ring-1 ring-gray-200 h-[280px]" />
  );

  return (
    <section className="w-full">
      <div className="max-w-7xl mx-auto px-4 relative">
        {title && (
          <div className="w-full mb-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold">{title}</h2>
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} />)}
          </div>
        )}

        {!loading && error && (
          <div className="p-4 rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-sm">
            Не удалось загрузить товары. Попробуйте позже.
          </div>
        )}

        {!loading && !error && itemsSafe.length > 0 && inStock.length === 0 && (
          <div className="p-4 rounded-lg bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-sm">
            Пока нет товаров в наличии.
          </div>
        )}

        {!loading && !error && inStock.length > 0 && (
          <div className="relative select-none">
            {/* стрелки */}
            <button
              type="button"
              onClick={() => go(-1)}
              className="hidden sm:flex absolute left-1 top-1/2 -translate-y-1/2 z-10
                         h-10 w-10 rounded-full bg-white shadow items-center justify-center
                         ring-1 ring-gray-200 hover:bg-gray-50 active:scale-95"
              aria-label="Назад"
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="hidden sm:flex absolute right-1 top-1/2 -translate-y-1/2 z-10
                         h-10 w-10 rounded-full bg-white shadow items-center justify-center
                         ring-1 ring-gray-200 hover:bg-gray-50 active:scale-95"
              aria-label="Вперёд"
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>

            {/* трек */}
            <div
              ref={contRef}
              className="overflow-hidden py-2"
              onMouseDown={(e) => onStart(e.clientX)}
              onMouseMove={(e) => onMove(e.clientX)}
              onMouseUp={onEnd}
              onMouseLeave={onEnd}
              onTouchStart={(e) => onStart(e.touches[0].clientX)}
              onTouchMove={(e) => onMove(e.touches[0].clientX)}
              onTouchEnd={onEnd}
            >
              <div
                ref={trackRef}
                className="flex"
                onTransitionEnd={(e) => {
                  if (e.propertyName === "transform") onTransitionEnd();
                }}
                style={{
                  gap: `${gap}px`,
                  transform: `translateX(${-(idx * (cardW + gap)) + dragX}px)`,
                  transition: withAnim ? "transform 360ms linear" : "transform 0s",
                  willChange: "transform",
                }}
              >
                {extended.map((p, i) => (
                  <div
                    key={`${p.id ?? i}-${i}`}
                    className="shrink-0 cursor-pointer"
                    style={{ width: `${cardW}px` }}
                    onClickCapture={preventLinkNav}
                    onClick={() => onCardClick(p)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onCardClick(p)}
                  >
                    <ProductCard p={p} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
